import { useState, useMemo } from 'react';
import * as AppPics from 'assets/images';
import { notifier } from 'components/Toaster';
import TablePaginate from 'components/Paginate';
import BasicDelete from 'components/molecules/Modal/Delete/BasicDelete';
import usePaginateDefaults from 'hooks/usePaginateDefaults';
import useDebounce from 'hooks/useDebounce';
import usePodcastDropdown from 'hooks/API/GET/usePodcastDropdown';
import usePodcastEpisodes from 'hooks/API/GET/usePodcastEpisodes';
import useGetPlayableFeedback from 'hooks/API/GET/useGetPlayableFeedback';
import { useDeletePlayableFeedback } from 'hooks/API/POST/useManagePlayableFeedback';
import PodcastDropdown from 'components/molecules/PodcastDropdown';
import StatusSelect from './StatusSelect';

/** Maps issueType keys to human-readable labels */
const ISSUE_TYPE_LABELS = {
  audio_issue: 'Audio Issue',
  wrong_content: 'Wrong Content',
  poor_quality: 'Poor Quality',
  missing_captions: 'Missing Captions',
  other: 'Other',
};

const ISSUE_TYPE_OPTIONS = Object.entries(ISSUE_TYPE_LABELS).map(([value, label]) => ({
  value,
  label,
}));

/** Badge colour per issue type */
const ISSUE_BADGE_CLASS = {
  audio_issue: 'badge-danger',
  wrong_content: 'badge-warning',
  poor_quality: 'badge-secondary',
  missing_captions: 'badge-info',
  other: 'badge-dark',
};

function PlaysFeedbackTab() {
  const [params, setParams] = usePaginateDefaults();
  const [searchTerm, setSearchTerm] = useState('');
  const [enableFilter, setEnableFilter] = useState(false);
  const [filterPodcastId, setFilterPodcastId] = useState('');
  const [filterEpisodeId, setFilterEpisodeId] = useState('');
  const [filterIssueType, setFilterIssueType] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  const debouncedSearch = useDebounce(searchTerm, 600);

  // ── Podcast dropdown ──────────────────────────────────────────────────────
  const { data: podcastDropdownData } = usePodcastDropdown({ enabled: true });
  const podcastOptions = podcastDropdownData?.data?.data ?? [];

  // ── Episode dropdown (depends on selected podcast) ────────────────────────
  // mode=dropdown → getEpisodesForDropdown → returns [{ label, value }] already react-select ready.
  // Gateway double-wraps: response.data.data = { data: [...] }, so episodeData.data is the array.
  const { data: episodeData } = usePodcastEpisodes({
    podcastId: filterPodcastId,
    configs: { params: { mode: 'dropdown' } },
    enabled: !!filterPodcastId,
  });
  const episodeOptions = Array.isArray(episodeData) ? episodeData : [];
  console.log('🚀 ~ PlaysFeedbackTab ~ episodeData:', episodeData);
  console.log('🚀 ~ PlaysFeedbackTab ~ episodeOptions:', episodeOptions);

  // ── Query params ──────────────────────────────────────────────────────────
  const queryParams = useMemo(
    () => ({
      ...params,
      keyword: debouncedSearch || undefined,
      podcastId: filterPodcastId || undefined,
      episodeId: filterEpisodeId || undefined,
      issueType: filterIssueType || undefined,
    }),
    [params, debouncedSearch, filterPodcastId, filterEpisodeId, filterIssueType]
  );

  const { data, isLoading } = useGetPlayableFeedback({
    enabled: true,
    config: { params: queryParams },
    queryKey: ['admin-playable-feedback', queryParams],
  });

  const feedbackList = data?.data?.result ?? [];
  const { page = 1, total = 0 } = data?.data?.meta ?? {};

  // ── Delete mutation ───────────────────────────────────────────────────────
  const { mutate: deleteFeedback } = useDeletePlayableFeedback();

  const handleDeleteConfirm = () => {
    deleteFeedback(deleteModal.id, {
      onSuccess: () => notifier.success('Feedback removed'),
      onError: () => notifier.error('Failed to remove feedback'),
    });
    setDeleteModal({ open: false, id: null });
  };

  // ── Filter helpers ────────────────────────────────────────────────────────
  const clearFilters = () => {
    setFilterPodcastId('');
    setFilterEpisodeId('');
    setFilterIssueType('');
    setSearchTerm('');
    setParams((prev) => ({ ...prev, page: 0 }));
    setEnableFilter(false);
  };

  const handlePodcastChange = (event) => {
    setFilterPodcastId(event.target.value);
    setFilterEpisodeId(''); // clear dependent episode filter
    setParams((p) => ({ ...p, page: 0 }));
  };

  return (
    <>
      {/* ── Delete confirm modal ── */}
      <BasicDelete
        open={deleteModal.open}
        closeModal={() => setDeleteModal({ open: false, id: null })}
        Header="Remove Feedback"
        Body="Are you sure you want to remove this feedback? This cannot be undone."
        handleConfirmation={handleDeleteConfirm}
      />

      {/* ── Search + Filter header ── */}
      <div className="tab__contentMenu mb-3">
        <ul className="tab__contentMenu-list">
          <li>
            <h2 className="prime-head p-0 boldtitle text-nowrap" style={{ fontSize: 16 }}>
              Plays Feedback
            </h2>
          </li>
        </ul>

        <div className="mainHeader__search">
          <ul className="tab__contentMenu-list">
            <li className="relative">
              <input
                className="mainHeader__searchInput"
                placeholder="Search by playfile, podcast or episode name…"
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setParams((prev) => ({ ...prev, page: 0 }));
                }}
              />
              <span className="mainHeader__searchBtn">
                <img src={AppPics.TopMenuSearchSvg} alt="Search" />
              </span>
            </li>
            <li className="px-4 flex items-center">
              <button
                type="button"
                onClick={() => setEnableFilter(!enableFilter)}
                className="flex items-center gap-1"
              >
                <img src={AppPics.TabFilterSvg} alt="Filter" />
                <span className="viewsettingtitle">Filter</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Filter panel ── */}
      {enableFilter && (
        <div className="row mt-2 mb-3">
          {/* Podcast filter */}
          <div className="col-lg-3">
            <PodcastDropdown
              version="v2"
              label="Podcast"
              options={podcastOptions}
              value={filterPodcastId}
              handleChange={handlePodcastChange}
              placeholder="All podcasts"
            />
          </div>

          {/* Episode filter — enabled only after a podcast is selected */}
          <div className="col-lg-3">
            <StatusSelect
              options={episodeOptions}
              value={filterEpisodeId}
              onChange={(v) => {
                setFilterEpisodeId(v);
                setParams((p) => ({ ...p, page: 0 }));
              }}
              label="Episode"
              isClearable
              placeholder={filterPodcastId ? 'All episodes' : 'Select podcast first'}
            />
          </div>

          {/* Issue type filter */}
          <div className="col-lg-3">
            <StatusSelect
              options={ISSUE_TYPE_OPTIONS}
              value={filterIssueType}
              onChange={(v) => {
                setFilterIssueType(v);
                setParams((p) => ({ ...p, page: 0 }));
              }}
              label="Issue Type"
              isClearable
              placeholder="All issue types"
            />
          </div>

          <div className="col-lg-3 d-flex" style={{ alignItems: 'flex-end' }}>
            <button type="button" className="btn btn-plane p--tint" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="tab__contentBody">
        <div className="tableWrap">
          <div className="table-responsive">
            <table className="table tableWrap__table">
              <thead className="thead-light __appTheadColor">
                <tr>
                  <th className="text-center">#</th>
                  <th>User</th>
                  <th>Playfile</th>
                  <th>Podcast / Episode</th>
                  <th className="text-center">Issue Type</th>
                  <th>Description</th>
                  <th className="text-center">Date</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      Loading…
                    </td>
                  </tr>
                ) : feedbackList.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-4">
                      No feedback found.
                    </td>
                  </tr>
                ) : (
                  feedbackList.map((item, idx) => (
                    <tr key={item._id}>
                      <td className="text-center">{(page - 1) * params.limit + idx + 1}</td>

                      {/* User — populated as item.user (not item.userId) */}
                      <td>
                        <div style={{ fontWeight: 500 }}>{item.user?.name ?? '—'}</div>
                        <small className="text-muted">
                          {item.user?.email ?? item.user?.mobilFull ?? ''}
                        </small>
                      </td>

                      {/* Playfile */}
                      <td style={{ maxWidth: 180 }}>
                        <div style={{ fontWeight: 500 }}>{item.playableId?.name ?? '—'}</div>
                      </td>

                      {/* Podcast / Episode */}
                      <td style={{ maxWidth: 200 }}>
                        <div style={{ fontWeight: 500 }}>{item.podcast?.name ?? '—'}</div>
                        <small className="text-muted">{item.episode?.name ?? ''}</small>
                      </td>

                      {/* Issue type badge */}
                      <td className="text-center">
                        <span
                          className={`badge ${
                            ISSUE_BADGE_CLASS[item.issueType] ?? 'badge-secondary'
                          }`}
                        >
                          {ISSUE_TYPE_LABELS[item.issueType] ?? item.issueType}
                        </span>
                      </td>

                      {/* Description */}
                      <td style={{ maxWidth: 240 }}>
                        <small className="text-muted">{item.description || <em>—</em>}</small>
                      </td>

                      {/* Date */}
                      <td className="text-center" style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                        {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}
                      </td>

                      {/* Actions */}
                      <td className="text-center">
                        <button
                          className="actions-icons text-danger"
                          title="Delete"
                          onClick={() => setDeleteModal({ open: true, id: item._id })}
                        >
                          <img src={AppPics.DeleteSvg} alt="Delete" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <TablePaginate
          currentPage={page}
          maxCount={total}
          defaultLimit={params.limit}
          setPage={(p) => setParams((prev) => ({ ...prev, page: p }))}
          handleChange={(limit) => setParams((prev) => ({ ...prev, limit, page: 0 }))}
        />
      </div>
    </>
  );
}

export default PlaysFeedbackTab;
